import { useMemo, useRef, useState } from 'react';
import { useClampOverflow } from '~/ui-rocco/hooks/use-clamp-overflow';

import { cn } from '@rocco/utils/cn';
import { addUtmToContentLinks } from '~/modules/tracking/util/add-utm-to-content-links';
import {
    DetailCreditColumnBlock,
    RICH_TEXT_HTML_CLASS,
} from '~/ui-rocco/pages/detail-shared';
import {
    htmlToPlainText,
    htmlToTeaserText,
} from '~/ui-rocco/pages/detail-shared/sentence-offsets';
import { useProjectLinkTracking } from '../hooks/use-project-link-tracking';
import { useAboutProject } from '../hooks/use-about-project';
import { ProjectSpecTable } from './AboutProjectRowContent';
import { useProjectCreditColumns } from './ProjectCreditsRow';

/** The body's reading type: the description as running text, no lead. */
const BODY = 'font-sans text-base leading-[1.4] font-normal text-[#e5e5e5]';

const TOGGLE =
    'mt-2 cursor-pointer border-0 bg-transparent p-0 text-left font-sans text-base leading-[1.4] font-normal text-[#a3a3a3]';

/**
 * The description on the phone, under the title: three lines, then Read more
 * for the rest, with the spec table behind it — the product page's
 * description, on this page's dark ground. The About-the-Project section
 * that carried the copy further down is not drawn on the phone.
 *
 * Collapsed, it is the plain text clamped to three lines, so the cut falls
 * on a line rather than on whatever paragraph the HTML happens to break at.
 * Open, it is the copy as written, links and all, with the project's facts
 * under it.
 */
export const MobileProjectDescription = ({
    fallbackText,
}: {
    /** The loader's summary, for a project whose Content is empty. */
    fallbackText?: string;
}) => {
    const { contentHtml, specItems } = useAboutProject();
    // The team and the suppliers, which the foot's credits row leaves to
    // this description on the phone.
    const creditColumns = useProjectCreditColumns().filter(
        (column) => column.entries.length > 0,
    );
    const trackContentLink = useProjectLinkTracking();
    const [expanded, setExpanded] = useState(false);
    const clampRef = useRef<HTMLParagraphElement>(null);

    const html = useMemo(
        () => (contentHtml ? addUtmToContentLinks(contentHtml) : ''),
        [contentHtml],
    );
    const plainText = useMemo(
        () => htmlToPlainText(html) || fallbackText || '',
        [html, fallbackText],
    );
    const teaser = useMemo(
        () => htmlToTeaserText(html) || fallbackText || '',
        [html, fallbackText],
    );
    const overflows = useClampOverflow(clampRef, teaser);

    const hasFacts = specItems.length > 0 || creditColumns.length > 0;
    if (!plainText && !hasFacts) return null;

    // Anything the three lines do not show: the rest of the copy, or the
    // facts that only live behind the toggle.
    const hasMore = overflows || hasFacts;

    return (
        <div>
            {expanded ? (
                html ? (
                    <div
                        className={cn(BODY, RICH_TEXT_HTML_CLASS)}
                        onClick={trackContentLink}
                        onAuxClick={trackContentLink}
                        onContextMenu={trackContentLink}
                        dangerouslySetInnerHTML={{ __html: html }}
                    />
                ) : plainText ? (
                    <p className={cn(BODY, 'm-0 whitespace-pre-line')}>
                        {plainText}
                    </p>
                ) : null
            ) : plainText ? (
                <p
                    ref={clampRef}
                    className={cn(BODY, 'm-0 line-clamp-3 whitespace-pre-line')}
                >
                    {teaser}
                </p>
            ) : null}

            {expanded && specItems.length > 0 ? (
                <div className={cn(plainText && 'mt-6')}>
                    <p className="m-0 mb-2 font-sans text-base leading-[1.4] font-medium text-[#e5e5e5]">
                        Project Details
                    </p>
                    <ProjectSpecTable items={specItems} />
                </div>
            ) : null}

            {expanded && creditColumns.length > 0 ? (
                <div className="mt-6 flex flex-col gap-6">
                    {creditColumns.map((column) => (
                        <DetailCreditColumnBlock
                            key={column.label}
                            column={column}
                            theme="dark"
                        />
                    ))}
                </div>
            ) : null}

            {hasMore ? (
                <button
                    type="button"
                    aria-expanded={expanded}
                    onClick={() => setExpanded((open) => !open)}
                    className={TOGGLE}
                >
                    {expanded ? 'Read less' : 'Read more'}
                </button>
            ) : null}
        </div>
    );
};
